import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'

import type { SidebarNavItem, SidebarNavSearchMenu } from '@/components/layout/app-shell'
import { useHubAgentName } from '@/features/shared/brand/branding-hooks'
import {
  AppMarketIcon,
  BusinessWorkspaceIcon,
  CoreAgentIcon,
} from '@/features/user/shell/navigation-icons'
import {
  listInstalledAppsForNavigation,
  type UserApp,
  type UserAppsListData,
} from '@/features/user/apps/api'
import {
  MOBILE_DRAWER_APP_LIST_LIMIT,
  selectVisibleNavigationApps,
  SIDEBAR_APP_LIST_EXPANDED_LIMIT,
  SIDEBAR_APP_LIST_LIMIT,
  sortMyAppsOrder,
  sortNavigationApps,
} from '@/features/user/apps/navigation-order'
import { useCoreAgentStatus } from '@/features/user/core-agent/use-status'
import {
  useBusinessNavigationLink,
  type BusinessNavigationLink,
} from '@/features/user/shell/business-navigation'
import { readStoredPortalAccount, type StoredPortalAccount } from '@/lib/auth/session-storage'
import { cn } from '@/lib/cn'

type ShellTranslate = (key: string) => string

const SIDEBAR_APP_SEARCH_DEBOUNCE_MS = 250
const sidebarAppCacheVersion = 3
const sidebarAppCacheKeyPrefix = 'aihub_user_sidebar_apps'
const sidebarAppListQueryKey = ['user-apps', 'sidebar-installed', 'all'] as const

export interface SidebarUserApp {
  app_icon?: string | null
  app_type: string
  avatar?: string | null
  id: string
  is_favorite: boolean
  is_installed: boolean
  name: string
  status: string
}

interface SidebarAppsListData extends Omit<UserAppsListData, 'items'> {
  items: SidebarUserApp[]
}

export interface SidebarInstalledAppsState {
  allApps: SidebarUserApp[]
  apps: SidebarUserApp[]
  /** 与「我的应用」页面一致的顺序，供默认跳转选取首个应用。 */
  myAppsOrder: SidebarUserApp[]
  error: boolean
  loading: boolean
  ready: boolean
  refetch: () => Promise<unknown>
  searchApps: SidebarUserApp[]
  searchLoading: boolean
  setSearchQuery: Dispatch<SetStateAction<string>>
}

interface SidebarAppCachePayload {
  items: SidebarUserApp[]
  version: typeof sidebarAppCacheVersion
}

function buildAppChatPath(appId: string) {
  const query = new URLSearchParams({ appId })

  return `/chat?${query.toString()}`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function sidebarAccountKey(account: StoredPortalAccount) {
  const accountId = account.id?.trim()
  const accountEmail = account.email?.trim()

  return accountId && accountId.length > 0
    ? accountId
    : accountEmail && accountEmail.length > 0
      ? accountEmail
      : 'anonymous'
}

function sidebarCacheKey(accountKey: string) {
  return `${sidebarAppCacheKeyPrefix}:${accountKey}`
}

function normalizeSidebarApp(value: unknown): SidebarUserApp | null {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.name !== 'string') {
    return null
  }

  return {
    app_icon: typeof value.app_icon === 'string' ? value.app_icon : null,
    app_type: typeof value.app_type === 'string' ? value.app_type : '',
    avatar: typeof value.avatar === 'string' ? value.avatar : null,
    id: value.id,
    is_favorite: value.is_favorite === true,
    is_installed: value.is_installed !== false,
    name: value.name,
    status: typeof value.status === 'string' ? value.status : 'active',
  }
}

function toSidebarApp(app: UserApp): SidebarUserApp {
  return {
    app_icon: app.app_icon ?? null,
    app_type: app.app_type,
    avatar: app.avatar ?? null,
    id: app.id,
    is_favorite: app.is_favorite,
    is_installed: app.is_installed,
    name: app.name,
    status: app.status,
  }
}

function readCachedSidebarApps(accountKey: string) {
  if (typeof window === 'undefined') {
    return []
  }

  try {
    const parsedPayload = JSON.parse(
      window.localStorage.getItem(sidebarCacheKey(accountKey)) ?? '',
    ) as Partial<SidebarAppCachePayload>

    if (parsedPayload.version !== sidebarAppCacheVersion || !Array.isArray(parsedPayload.items)) {
      return []
    }

    return sortNavigationApps(
      parsedPayload.items
        .map((item) => normalizeSidebarApp(item))
        .filter((item): item is SidebarUserApp => item !== null),
    ).slice(0, MOBILE_DRAWER_APP_LIST_LIMIT)
  } catch {
    return []
  }
}

function writeCachedSidebarApps(accountKey: string, apps: SidebarUserApp[]) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const payload: SidebarAppCachePayload = {
      items: sortNavigationApps(
        apps
          .map((app) => normalizeSidebarApp(app))
          .filter((app): app is SidebarUserApp => app !== null),
      ).slice(0, MOBILE_DRAWER_APP_LIST_LIMIT),
      version: sidebarAppCacheVersion,
    }

    window.localStorage.setItem(sidebarCacheKey(accountKey), JSON.stringify(payload))
  } catch {
    // 缓存只用于避免 sidebar 闪烁，受限存储环境直接降级到 query cache。
  }
}

function useDebouncedSearch(value: string) {
  const [search, setSearch] = useState(value.trim())

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(value.trim())
    }, SIDEBAR_APP_SEARCH_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [value])

  return search
}

function renderSidebarAppIcon(app: SidebarUserApp) {
  const initial = Array.from(app.name.trim())[0]?.toUpperCase() ?? '?'
  const iconUrl = app.avatar ?? app.app_icon
  const className = 'size-5 rounded-[4px] object-cover'

  if (iconUrl) {
    return (
      <img alt="" aria-hidden className={className} decoding="async" loading="lazy" src={iconUrl} />
    )
  }

  return (
    <span
      aria-hidden
      className={cn(
        className,
        'inline-flex items-center justify-center bg-[#e2e9ff] text-[11px] font-semibold text-[var(--color-primary)]',
      )}
    >
      {initial}
    </span>
  )
}

function renderSidebarAppSearchIcon(app: SidebarUserApp) {
  return renderSidebarAppIcon(app)
}

function renderSidebarAppNavIcon(app: SidebarUserApp) {
  return renderSidebarAppIcon(app)
}

export function makeUserAppSearchMenu({
  apps,
  loading,
  onQueryChange,
  t,
}: {
  apps: SidebarUserApp[]
  loading: boolean
  onQueryChange?: (query: string) => void
  t: ShellTranslate
}): SidebarNavSearchMenu {
  return {
    emptyLabel: t('userNavigation.appSearch.empty'),
    filterMode: 'server',
    noMatchesLabel: t('userNavigation.appSearch.noMatches'),
    inputLabel: t('userNavigation.appSearch.label'),
    items: apps.map((app) => ({
      icon: renderSidebarAppSearchIcon(app),
      key: app.id,
      label: app.name,
      path: buildAppChatPath(app.id),
    })),
    label: t('userNavigation.appSearch.label'),
    loading,
    loadingLabel: t('userNavigation.appSearch.loading'),
    onQueryChange,
    placeholder: t('userNavigation.appSearch.placeholder'),
  }
}

export function makeUserInstalledAppNavItems({
  apps,
  desktopLimit = SIDEBAR_APP_LIST_LIMIT,
  loading,
  mobileApps = apps,
  t,
}: {
  apps: SidebarUserApp[]
  desktopLimit?: number
  loading: boolean
  mobileApps?: SidebarUserApp[]
  t: ShellTranslate
}): SidebarNavItem[] {
  const quickAppsHeading: SidebarNavItem = {
    children: apps.map((app) => ({
      key: `sidebar-installed-app-flyout-${app.id}`,
      label: app.name,
      path: buildAppChatPath(app.id),
    })),
    defaultExpanded: true,
    key: 'quick-apps-heading',
    kind: 'heading',
    label: t('userNavigation.quickApps'),
    mobileChildren: mobileApps.slice(0, MOBILE_DRAWER_APP_LIST_LIMIT).map((app) => ({
      icon: renderSidebarAppNavIcon(app),
      key: `mobile-installed-app-${app.id}`,
      label: app.name,
      path: buildAppChatPath(app.id),
    })),
    spacingBefore: 'section',
  }

  if (loading && apps.length === 0) {
    return [
      quickAppsHeading,
      {
        // 占位项复用 /apps 让它可点击，但不能和应用入口抢选中态。
        activeBehavior: 'never',
        key: 'sidebar-installed-apps-loading',
        label: t('userNavigation.quickAppsLoading'),
        path: '/apps',
        section: 'quick-apps-heading',
        variant: 'subtle',
      },
    ]
  }

  if (apps.length === 0) {
    return [
      quickAppsHeading,
      {
        activeBehavior: 'never',
        key: 'sidebar-installed-apps-empty',
        label: t('userNavigation.noQuickApps'),
        path: '/apps',
        section: 'quick-apps-heading',
        variant: 'subtle',
      },
    ]
  }

  return [
    quickAppsHeading,
    ...apps.slice(0, desktopLimit).map((app) => ({
      icon: renderSidebarAppNavIcon(app),
      key: `sidebar-installed-app-${app.id}`,
      label: app.name,
      path: buildAppChatPath(app.id),
      section: 'quick-apps-heading',
      variant: 'app' as const,
    })),
  ]
}

export function makeBusinessNavigationItems(
  businessNavigationLink: BusinessNavigationLink | null,
): SidebarNavItem[] {
  if (!businessNavigationLink) {
    return []
  }

  return [
    {
      activeBehavior: 'never',
      external: true,
      icon: <BusinessWorkspaceIcon />,
      key: 'business-navigation',
      label: businessNavigationLink.label,
      path: businessNavigationLink.url,
      sticky: 'top',
      variant: 'ghost-prominent',
    },
  ]
}

export function useSidebarInstalledApps(currentAppId?: string): SidebarInstalledAppsState {
  const account = useMemo(() => readStoredPortalAccount('user'), [])
  const accountKey = useMemo(() => sidebarAccountKey(account), [account])
  const [searchInput, setSearchInput] = useState('')
  const normalizedSearchInput = searchInput.trim()
  const debouncedSearch = useDebouncedSearch(normalizedSearchInput)
  const sidebarAppsQuery = useQuery({
    gcTime: 10 * 60 * 1000,
    initialData: (): SidebarAppsListData | undefined => {
      const cachedApps = readCachedSidebarApps(accountKey)

      if (cachedApps.length === 0) {
        return undefined
      }

      return {
        items: cachedApps,
        page: null,
        page_size: null,
        total: cachedApps.length,
      }
    },
    initialDataUpdatedAt: 0,
    placeholderData: (previousData) => previousData,
    queryFn: async (): Promise<SidebarAppsListData> => {
      const data = await listInstalledAppsForNavigation()

      // `/app/my-apps` 已按 installed collection 过滤，前端不再重复判断 is_installed。
      return {
        ...data,
        items: data.items.map((app) => toSidebarApp(app)),
      }
    },
    queryKey: sidebarAppListQueryKey,
    staleTime: 60_000,
  })
  const sidebarAppSearchQuery = useQuery({
    enabled: debouncedSearch.length > 0,
    gcTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SidebarAppsListData> => {
      const data = await listInstalledAppsForNavigation({ search: debouncedSearch })

      // `/app/my-apps` 已按 installed collection 过滤，前端不再重复判断 is_installed。
      return {
        ...data,
        items: data.items.map((app) => toSidebarApp(app)),
      }
    },
    queryKey: ['user-apps', 'sidebar-search', debouncedSearch],
    staleTime: 30_000,
  })

  useEffect(() => {
    if (sidebarAppsQuery.data?.items) {
      writeCachedSidebarApps(accountKey, sortNavigationApps(sidebarAppsQuery.data.items))
    }
  }, [accountKey, sidebarAppsQuery.data?.items])

  const allApps = useMemo(
    () => sortNavigationApps(sidebarAppsQuery.data?.items ?? []),
    [sidebarAppsQuery.data?.items],
  )
  const apps = useMemo(
    () => selectVisibleNavigationApps(allApps, currentAppId),
    [allApps, currentAppId],
  )
  const myAppsOrder = useMemo(
    () => sortMyAppsOrder(sidebarAppsQuery.data?.items ?? []),
    [sidebarAppsQuery.data?.items],
  )
  const searchApps = useMemo(
    () =>
      normalizedSearchInput.length > 0 && debouncedSearch.length > 0
        ? sortNavigationApps(sidebarAppSearchQuery.data?.items ?? []).slice(
            0,
            SIDEBAR_APP_LIST_LIMIT,
          )
        : apps,
    [apps, debouncedSearch.length, normalizedSearchInput.length, sidebarAppSearchQuery.data?.items],
  )

  return {
    allApps,
    apps,
    error: sidebarAppsQuery.isError,
    loading: sidebarAppsQuery.isLoading && !sidebarAppsQuery.data,
    myAppsOrder,
    ready: sidebarAppsQuery.dataUpdatedAt > 0,
    refetch: async () => sidebarAppsQuery.refetch(),
    searchApps,
    searchLoading:
      normalizedSearchInput.length > 0
        ? normalizedSearchInput !== debouncedSearch || sidebarAppSearchQuery.isFetching
        : sidebarAppsQuery.isLoading && apps.length === 0,
    setSearchQuery: setSearchInput,
  }
}

export function useUserNavigation(): SidebarNavItem[] {
  const { t } = useTranslation('shell')
  const businessNavigationLink = useBusinessNavigationLink()
  const hubAgentName = useHubAgentName()
  const hubAgentStatus = useCoreAgentStatus()
  const sidebarApps = useSidebarInstalledApps()
  const installedAppItems = useMemo(
    () =>
      makeUserInstalledAppNavItems({
        apps: selectVisibleNavigationApps(
          sidebarApps.allApps,
          undefined,
          SIDEBAR_APP_LIST_EXPANDED_LIMIT,
        ),
        desktopLimit: SIDEBAR_APP_LIST_EXPANDED_LIMIT,
        loading: sidebarApps.loading,
        mobileApps: selectVisibleNavigationApps(
          sidebarApps.allApps,
          undefined,
          MOBILE_DRAWER_APP_LIST_LIMIT,
        ),
        t,
      }),
    [sidebarApps.allApps, sidebarApps.loading, t],
  )

  return useMemo(
    () => [
      ...(hubAgentStatus.data?.enabled
        ? [
            {
              activeMatch: 'hub-chat' as const,
              icon: <CoreAgentIcon />,
              key: 'core-agent',
              label: hubAgentName,
              path: '/chat',
              sticky: 'top' as const,
              variant: 'ghost-prominent' as const,
            },
          ]
        : []),
      {
        icon: <AppMarketIcon />,
        key: 'app-market',
        label: t('userNavigation.appMarket'),
        path: '/apps',
        sticky: 'top',
        variant: 'ghost-prominent',
      },
      ...makeBusinessNavigationItems(businessNavigationLink),
      ...installedAppItems,
    ],
    [businessNavigationLink, hubAgentName, hubAgentStatus.data?.enabled, installedAppItems, t],
  )
}
